import { SpaceType } from 'src/components/SearchBar/types'
import { haversineKm } from 'src/constants/onDemandCoverage'
import {
  PillarPage,
  SEARCH_PAGE_CITY_MAP,
  SearchPageCity
} from 'src/constants/searchPages'
import { ICoordinates, ILocation, LocationType } from 'src/types/graphql'
import { canonicalCity } from 'src/utils/pixels/common'

/**
 * Directory listings — real, UNCLAIMED storage & parking facilities imported
 * from DENUE/INEGI, published below the search tier floor and claimable in
 * place (docs/plans/2026-08-20-001-feature-directory-listings-plan.md).
 *
 * Pure routing + presentation helpers. No Apollo, no React — the ficha, the
 * hubs, the card and the sitemap all resolve URLs through here so the shape of
 * a directory URL is defined once.
 */

/** URL segment for the `[categoria]` route param. */
export enum DirectoryCategory {
  Storage = 'bodegas',
  Parking = 'estacionamientos'
}

/**
 * Which `LocationType`s a category asks the backend for. Directory rows carry
 * exactly ONE unpriced configuration whose only job is to make the category
 * filterable through the nested `configurations.locationType` path, and the
 * importer writes exactly these two values — so this map is total, not a
 * best-effort classification of the wider LocationType enum.
 */
export const DIRECTORY_CATEGORY_LOCATION_TYPES: Record<
  DirectoryCategory,
  LocationType[]
> = {
  [DirectoryCategory.Storage]: [LocationType.SelfStorageUnit],
  [DirectoryCategory.Parking]: [LocationType.Parking]
}

/**
 * Verified-supply pillar a category's renter should be routed to — used by the
 * "Espacios verificados SpotMe cerca" module and by the CAPPED reveal state,
 * so a renter is never left with nothing (plan §1.4).
 */
export const DIRECTORY_CATEGORY_PILLAR: Record<DirectoryCategory, PillarPage> =
  {
    [DirectoryCategory.Storage]: PillarPage.SelfStorage,
    [DirectoryCategory.Parking]: PillarPage.Parking
  }

/** Category a directory row belongs to, from its single configuration. */
export function directoryCategoryFromLocation(
  location: Pick<ILocation, 'defaultConfiguration'> | undefined | null
): DirectoryCategory | null {
  const locationType = location?.defaultConfiguration?.locationType
  if (locationType === LocationType.Parking) return DirectoryCategory.Parking
  if (locationType === LocationType.SelfStorageUnit) {
    return DirectoryCategory.Storage
  }
  return null
}

/**
 * Longest title fragment kept in a ficha slug. The slug's job is to be
 * readable and keyword-bearing; the trailing id is what actually resolves it,
 * so truncating cannot break a URL.
 */
const MAX_SLUG_TITLE_CHARS = 60

/**
 * Slugifier for directory URLs. Starts from the backend's `slugify`
 * (api/location/utils.ts) character rules and then DELIBERATELY DIVERGES with
 * a final ASCII pass.
 *
 * The divergence is load-bearing. The backend's version leaves any character
 * it does not explicitly list — including the EN DASH in real imported titles
 * like "U-Storage Anzures – Polanco". A slug carrying one produces a URL that
 * cannot be put in a `Location` header (`ERR_INVALID_CHAR`), so the ficha's
 * canonical redirect 500s and the page is unreachable. Everything outside
 * `[a-z0-9-]` therefore becomes a separator here.
 */
export function slugifyDirectory(value: string): string {
  return (
    value
      .normalize('NFD')
      .replaceAll(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replaceAll(/[,;:()'"!?#@&={}[\]|\\<>]/g, '')
      .replaceAll(/[+]/g, '-')
      .replaceAll(/[\s_.]+/g, '-')
      // Everything still outside the URL-safe set (en/em dashes, °, ª, ñ that
      // survived NFD, CJK, …) becomes a separator rather than riding into a
      // header.
      .replaceAll(/[^a-z0-9-]+/g, '-')
      .replaceAll(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '')
  )
}

const UUID_SUFFIX_RE =
  /([\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12})$/i

/**
 * The ficha slug is `{readable-title}-{locationId}`.
 *
 * The id is IN the slug on purpose. Directory rows have no indexed slug field
 * (the ES mapping is `dynamic: false`, additions-only, and shared with prod),
 * so a slug-keyed lookup would need a mapping push; carrying the id lets the
 * ficha resolve through the existing public `Query.location` branch with no
 * schema change. It also matches the listing-URL precedent, which already ends
 * in a raw configuration id.
 */
export function buildDirectoryFichaSlug(
  location: Pick<ILocation, 'id' | 'title'>
): string {
  const titlePart = slugifyDirectory(location.title ?? '').slice(
    0,
    MAX_SLUG_TITLE_CHARS
  )
  const trimmed = titlePart.replace(/-+$/, '')
  return trimmed ? `${trimmed}-${location.id}` : location.id
}

/** The locationId a ficha slug resolves to, or null when the slug is malformed. */
export function locationIdFromDirectorySlug(
  slug: string | string[] | undefined
): string | null {
  const value = Array.isArray(slug) ? slug[0] : slug
  if (!value) return null
  const match = UUID_SUFFIX_RE.exec(value)
  return match ? match[1].toLowerCase() : null
}

/**
 * The ficha href for a directory row.
 *
 * NEVER link a directory row through `Location.urlRoute` / `canonicalUrl`:
 * those resolve to a LISTING-page URL whose by-configuration builders refuse
 * directory rows, so the link would render a 404 for every renter. Build the
 * ficha href here instead.
 *
 * Returns null when the row cannot be routed (outside every hub metro, or an
 * unknown category) — callers render the card without a link rather than a
 * dead one.
 */
export function buildDirectoryFichaHref(
  location: Pick<
    ILocation,
    'id' | 'title' | 'coordinates' | 'addressDetails' | 'defaultConfiguration'
  >
): string | null {
  const category = directoryCategoryFromLocation(location)
  if (!category) return null
  const citySlug = directoryHubCityForLocation(location)
  if (!citySlug) return null
  const pillar = DIRECTORY_CATEGORY_PILLAR[category]
  return `/${pillar}/${citySlug}/directorio/${buildDirectoryFichaSlug(location)}`
}

/**
 * The `directorio` path segment that separates a ficha from a hood search.
 *
 * Next resolves a STATIC segment before a dynamic one at the same position, so
 * `/{pillar}/{city}/directorio/{slug}` wins over `[hood]/[configId]` without
 * ambiguity — and `/{pillar}/{city}/directorio` on its own already 404s,
 * because `directorio` is not in `HOOD_MAP`.
 */
/**
 * One entry in a ficha's related-links block (`RelatedFichas`).
 *
 * A flat, pre-resolved shape on purpose: the block is a handful of anchors, so
 * shipping whole `ILocation` rows into `__NEXT_DATA__` would pay a
 * `DirectoryCardFields` payload each for a name and an href.
 */
export interface ISibling {
  href: string
  name: string
  area: string | null
}

export const DIRECTORY_PATH_SEGMENT = 'directorio'

/**
 * The directory category a pillar carries rows for, or `null` for the B2B
 * pillars the directory has no data for. The inverse of
 * `DIRECTORY_CATEGORY_PILLAR`, used to validate the `[pillar]` route param.
 */
export function directoryCategoryForPillar(
  pillar: string | string[] | undefined
): DirectoryCategory | null {
  const value = Array.isArray(pillar) ? pillar[0] : pillar
  if (value === PillarPage.SelfStorage) return DirectoryCategory.Storage
  if (value === PillarPage.Parking) return DirectoryCategory.Parking
  return null
}

/**
 * The metros the directory routes fichas under — the `[ciudad]` segment of
 * every ficha URL is one of these. Each MUST have a `center` on its
 * `SEARCH_PAGE_CITY_MAP` entry, which is what `directoryHubCityForLocation`
 * measures distance against; an entry without one silently never wins and its
 * facilities render unlinked cards.
 *
 * (Named "hub" from the removed browsable hub pages; the concept it now names
 * is the ficha's parent metro.)
 *
 * The first three are the 2026-08-23 launch metros; the rest are the
 * 2026-08-24 national expansion. Adding one here is the client half of the
 * add-a-metro change — the server half is `DIRECTORY_METROS` in
 * spotme-server/src/api/directory/directoryPublishGate.ts (see its comment
 * for the full four-surface list). Nearest-center-within-80km resolves the
 * hub, so neighboring hubs (Saltillo↔Monterrey, Toluca↔CDMX,
 * Cancún↔Playa del Carmen↔Cozumel) coexist without explicit boundaries.
 */
export const DIRECTORY_HUB_CITIES = [
  'monterrey',
  'ciudad-de-mexico',
  'guadalajara',
  'acapulco',
  'aguascalientes',
  'cancun',
  'chihuahua',
  'ciudad-del-carmen',
  'ciudad-juarez',
  'ciudad-obregon',
  'cozumel',
  'culiacan',
  'ensenada',
  'guaymas',
  'hermosillo',
  'la-paz',
  'leon',
  'los-cabos',
  'los-mochis',
  'mazatlan',
  'merida',
  'mexicali',
  'pachuca',
  'playa-del-carmen',
  'puebla',
  'puerto-vallarta',
  'queretaro',
  'reynosa',
  'saltillo',
  'san-luis-potosi',
  'tampico',
  'tijuana',
  'toluca',
  'torreon',
  'veracruz',
  // 2026-08-24 wave-2 national expansion — every slug below MUST carry a
  // `center` on its SEARCH_PAGE_CITY_MAP entry or the hub routing is inert.
  'campeche',
  'celaya',
  'chetumal',
  'ciudad-victoria',
  'coatzacoalcos',
  'colima',
  'cordoba',
  'cuautla',
  'cuernavaca',
  'durango',
  'irapuato',
  'manzanillo',
  'matamoros',
  'monclova',
  'morelia',
  'nogales',
  'nuevo-laredo',
  'oaxaca',
  'orizaba',
  'piedras-negras',
  'poza-rica',
  'tapachula',
  'tehuacan',
  'tepic',
  'tlaxcala',
  'tuxtla-gutierrez',
  'uruapan',
  'villahermosa',
  'xalapa',
  'zacatecas',
  'zamora'
] as const

export type DirectoryHubCity = (typeof DIRECTORY_HUB_CITIES)[number]

export function isDirectoryHubCity(
  value: string | string[] | undefined
): value is DirectoryHubCity {
  return (
    typeof value === 'string' &&
    (DIRECTORY_HUB_CITIES as readonly string[]).includes(value)
  )
}

/**
 * How far from a metro's center a facility may sit and still route under it.
 *
 * Derived, not guessed: at zoom 10 a geo box around the center is ±63 km
 * east-west and ±49 km north-south, whose farthest corner is ~80 km out. A
 * facility beyond that gets no ficha link at all rather than one filed under a
 * metro it is not in.
 */
export const DIRECTORY_HUB_RADIUS_KM = 80

export function directoryHubCenter(
  city: DirectoryHubCity
): ICoordinates | undefined {
  return SEARCH_PAGE_CITY_MAP[city as keyof typeof SEARCH_PAGE_CITY_MAP]?.center
}

export function directoryCityName(city: string): string {
  return (
    SEARCH_PAGE_CITY_MAP[city as keyof typeof SEARCH_PAGE_CITY_MAP]?.cityName ??
    city
  )
}

/**
 * Monogram for the brand tile when the facility has no logo. One letter, from
 * the chain when the facility is a branch so every U-Storage branch reads the
 * same, otherwise from the title.
 */
export function directoryMonogram(
  location: Pick<ILocation, 'title' | 'directoryMeta'>
): string {
  const explicit = location.directoryMeta?.monogram?.trim()
  if (explicit) return explicit.slice(0, 1).toUpperCase()
  const source = location.directoryMeta?.chain?.trim() || location.title || ''
  const firstLetter = source.replace(/[^\p{L}\p{N}]/gu, '').slice(0, 1)
  return firstLetter ? firstLetter.toUpperCase() : '·'
}

/**
 * Brand tile color. Only `#rgb` / `#rrggbb` is accepted — the value comes from
 * scraped operator sites, and it is interpolated into a style attribute, so an
 * unvalidated string would be an injection point.
 */
const HEX_COLOR_RE = /^#(?:[\da-f]{3}|[\da-f]{6})$/i

export function directoryBrandColor(
  location: Pick<ILocation, 'directoryMeta'>
): string | null {
  const raw = location.directoryMeta?.brandColor?.trim()
  if (!raw || !HEX_COLOR_RE.test(raw)) return null
  return raw
}

/**
 * Space-type filter a category maps to on the generic `/search` route, for
 * facilities whose municipality has no city landing page of its own.
 */
const DIRECTORY_CATEGORY_SPACE_TYPE: Record<DirectoryCategory, SpaceType> = {
  [DirectoryCategory.Storage]: SpaceType.SELF_STORAGE,
  [DirectoryCategory.Parking]: SpaceType.PARKING
}

/**
 * Where a renter goes when we send them to VERIFIED supply — from the ficha's
 * "Espacios verificados SpotMe cerca" module, and from the CAPPED reveal
 * state, whose whole job is that the renter is never left with nothing.
 *
 * Prefers the pillar + city landing page (a real, indexed, SSG'd surface with
 * its own copy) and falls back to a geo-anchored `/search` for the many
 * municipalities that have no landing page — a directory row can sit in
 * Tlalnepantla or Tlaquepaque, which are not all `SearchPageCity` entries.
 */
export function buildVerifiedNearbyHref(
  category: DirectoryCategory,
  citySlug: string | undefined,
  coordinates?: { lat: number; lng: number } | null
): string {
  const pillar = DIRECTORY_CATEGORY_PILLAR[category]
  const isLandingCity =
    !!citySlug &&
    citySlug !== SearchPageCity.Base &&
    (Object.values(SearchPageCity) as string[]).includes(citySlug)
  if (isLandingCity) return `/${pillar}/${citySlug}`
  if (coordinates) {
    const params = new URLSearchParams({
      lat: String(coordinates.lat),
      lng: String(coordinates.lng),
      zoom: '13',
      spaceTypes: DIRECTORY_CATEGORY_SPACE_TYPE[category]
    })
    return `/search?${params.toString()}`
  }
  return `/${pillar}`
}

/**
 * A facility's hub metro, resolved by GEOGRAPHY rather than by its
 * `addressDetails.city`.
 *
 * This is not a stylistic choice — `addressDetails.city` holds the MUNICIPIO,
 * which for the whole of Mexico City is an alcaldía ("Miguel Hidalgo",
 * "Cuauhtémoc", "Álvaro Obregón") and never the string "Ciudad de México".
 * Slugifying it produced `/directorio/miguel-hidalgo/...`, which is not a hub
 * city, so every CDMX card linked to a 404. The same applies to the Edomex
 * ZMVM municipalities and to the Monterrey and Guadalajara metro areas.
 *
 * Resolving by distance to the hub centers makes the ficha URL agree with the
 * query that produced the card: the hub itself selects rows with a geo box
 * around the same center, so anything a hub can return also routes to it.
 * The three metros are hundreds of km apart, so "nearest center" is never
 * ambiguous.
 */
export function directoryHubCityForLocation(
  location: Pick<ILocation, 'coordinates' | 'addressDetails'>
): DirectoryHubCity | null {
  const { coordinates } = location
  if (coordinates) {
    let best: { city: DirectoryHubCity; km: number } | null = null
    for (const city of DIRECTORY_HUB_CITIES) {
      const center = directoryHubCenter(city)
      if (!center) continue
      const km = haversineKm(
        coordinates.lat,
        coordinates.lng,
        center.lat,
        center.lng
      )
      if (!best || km < best.km) best = { city, km }
    }
    if (best && best.km <= DIRECTORY_HUB_RADIUS_KM) return best.city
  }
  // Fallback for a row with no coordinates: the municipality itself is
  // occasionally already a hub city (a facility whose address says
  // "Monterrey" / "Guadalajara" outright).
  const bySlug = canonicalCity(location.addressDetails?.city)
  return isDirectoryHubCity(bySlug) ? bySlug : null
}

/**
 * Longest string still plausibly an opening-hours line. Real values look like
 * "Lun–Vie 9:00–19:00, Sáb 9:00–14:00"; anything much longer is scraped page
 * text that happened to sit near the hours block.
 */
const MAX_HOURS_CHARS = 90

/** Any currency amount. */
const CONTAINS_PRICE_RE = /\$\s*\d/

/**
 * Display-safe opening hours, or null.
 *
 * `directoryMeta.hours` is scraped from operators' own sites and the crawl is
 * imperfect — live dev data contains values like
 * `"la Ver Bodegas DESDE: $669.04 Querétaro Ver Bodegas En U-St"`. Two reasons
 * that must not reach a ficha:
 *
 *   1. It renders as visible garbage under a heading that says "Horario".
 *   2. More seriously, it smuggles a PRICE onto a public page through a field
 *      that carries no source and no capture date. Every price the directory
 *      shows goes through `priceFromMxn`, which the server nulls once the
 *      capture is 45 days old and which the ficha always renders with its
 *      attribution line. A price leaking in through `hours` bypasses both —
 *      exactly the code↔marketing drift the four-vertex rule exists to stop.
 *
 * Rejecting is the safe default: a missing hours line costs a renter nothing,
 * a wrong one costs credibility.
 */
export function displayableDirectoryHours(
  hours: string | null | undefined
): string | null {
  const value = hours?.trim()
  if (!value) return null
  if (value.length > MAX_HOURS_CHARS) return null
  if (CONTAINS_PRICE_RE.test(value)) return null
  // Scraped-page chrome that survived the length cap: an email, a copyright
  // line or a URL next to the hours block is never opening hours ("…Domingo
  // Cerrado Acceso a bode" shipped to a live ficha 2026-08-24 as a truncated
  // "Acceso a bodegas" tail; its siblings carried "facturacion@…" and "© 2022
  // …"). The CSV is cleaned at the source too — this is the belt to that
  // suspenders.
  if (/[@©]|https?:\/\//.test(value)) return null
  return value
}

/**
 * Which `LocationType`s the tier floor should ask for, given the search's
 * effective space types.
 *
 * This is not cosmetic filtering — without it the floor passes
 * `locationTypes: undefined` and shows STORAGE facilities on a parking search
 * and vice versa. Pillar pages set `spaceTypes` (and `sizes`), never
 * `locationTypes`, so the floor has to derive it.
 *
 * Returns `null` to mean "render no floor at all". The directory only holds
 * self-storage and parking rows, so a warehouse / nave / land search has
 * nothing honest to show — an empty result is the right answer there, not a
 * list of mini-bodegas under a heading about industrial space.
 */
export function directoryLocationTypesForSpaceTypes(
  spaceTypes: SpaceType[] | undefined
): LocationType[] | undefined | null {
  // No filter, or the explicit "all" — the floor may show both categories.
  if (!spaceTypes || spaceTypes.length === 0) return undefined
  if (spaceTypes.includes(SpaceType.ALL)) return undefined

  const types: LocationType[] = []
  if (spaceTypes.includes(SpaceType.SELF_STORAGE)) {
    types.push(...DIRECTORY_CATEGORY_LOCATION_TYPES[DirectoryCategory.Storage])
  }
  if (spaceTypes.includes(SpaceType.PARKING)) {
    types.push(...DIRECTORY_CATEGORY_LOCATION_TYPES[DirectoryCategory.Parking])
  }
  return types.length > 0 ? types : null
}

/**
 * A stable key for "the same operator", used only for the diversity cap.
 *
 * `directoryMeta.chain` alone is not enough: only 106 of 679 storage rows in
 * the source data carry one, so recognisable brands the CSV never tagged —
 * DAY STORE is the loudest, with 18 of 48 rows in one CDMX viewport — read as
 * unrelated independents and take the whole section. Falling back to the first
 * two words of the title catches them.
 *
 * Chain and title go through the SAME normalisation on purpose: the chain
 * value "U-Storage" and the scraped title "U-STORAGE PERIFERICO" have to
 * collapse to one key, or the brand is capped twice and still dominates.
 *
 * Generic names ("MINI BODEGAS") collapse together too. That is intended —
 * three cards with the same title look like a bug to a renter whether or not
 * they are the same company.
 */
export function directoryBrandKey(location: ILocation): string {
  const chain = location.directoryMeta?.chain?.trim()
  const source = chain || location.title || ''
  const words = source
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
  return words.slice(0, 2).join(' ') || location.id
}

/**
 * Does this facility's own registered name say "pensión"?
 *
 * DENUE's 812410 lumps hourly public lots and monthly pensiones into one
 * code, and only ~15% of the live parking rows self-declare "pensión" in
 * their name (measured 2026-08-24: 288 of 1,810). That self-declaration is
 * the ONLY monthly-parking signal we hold, so it is surfaced exactly as far
 * as it goes: a chip on the card and first position on the pensión pillar's
 * floor. NEVER present the rest as pensiones — whether an hourly lot rents
 * monthly cajones is exactly what the claim funnel exists to find out.
 */
export function isPensionNamed(title: string | null | undefined): boolean {
  return /pensi[oó]n|pencion/i.test(title ?? '')
}

/**
 * Scraped size lists arrive in whatever order the operator's site listed them
 * ("17 m² | 50 m² | 9 m²", "100 m² | 2 m²"). Sort pipe-separated single
 * values ascending so the line reads as a range a renter can scan. Anything
 * that is not purely `<number> m²` segments — ranges ("3–60 m²"), slash lists
 * ("3/4.5/9 m²"), free text — is returned untouched: those shapes are already
 * ordered at the source, and re-writing text we merely scraped risks changing
 * its meaning.
 */
export function sortDirectorySizes(sizes: string): string {
  const segments = sizes.split('|').map((s) => s.trim())
  if (segments.length < 2) return sizes
  const parsed = segments.map((segment) => {
    const match = segment.match(/^(\d+(?:\.\d+)?)\s*m²?$/)
    return match ? Number(match[1]) : null
  })
  if (parsed.some((value) => value === null)) return sizes
  return segments
    .map((segment, i) => ({ segment, value: parsed[i] as number }))
    .sort((a, b) => a.value - b.value)
    .map((entry) => entry.segment)
    .join(' | ')
}

/**
 * ── Ficha indexability + SEO text (the 2026-08-25 noindex lift, Jules) ──
 *
 * Launch metros HELD BACK from the index — the phased rollout's control
 * group. These are the only markets where directory fichas and monetized
 * pillar/city pages share real supply, so any SERP cannibalization would
 * show here first. Everywhere else the ficha is the only SpotMe page that
 * CAN rank. Revisit with GSC evidence ~2026-10-26 (Jules: "lets not forget
 * in a couple of months"). EDOMEX/ZMVM facilities hub under
 * `ciudad-de-mexico` (nearest-center routing), so this set covers them too.
 */
export const DIRECTORY_INDEX_HELD_HUB_CITIES: readonly DirectoryHubCity[] = [
  'monterrey',
  'ciudad-de-mexico',
  'guadalajara'
]

/**
 * Which fichas earn an index slot — the Yelp-style quality gate.
 *
 * Storage rows always qualify: every live one is a named business with
 * contact/hours/site depth. Parking rows qualify only with a contact AND at
 * least one richness signal (display-safe hours, a fresh desde-price, or a
 * real logo) — a name-and-pin page is exactly the thin-content drag the plan
 * lists as risk #4, and DENUE parking is mostly name-and-pin today. The gate
 * opens per row as claims and crawls enrich it; no code change needed.
 *
 * Used by BOTH the ficha's `noindex` flip and the sitemap — one policy, two
 * consumers, no drift.
 */
export function isDirectoryFichaIndexable(args: {
  citySlug: string
  category: DirectoryCategory
  location: Pick<ILocation, 'directoryHasContact' | 'directoryMeta'>
}): boolean {
  if (
    (DIRECTORY_INDEX_HELD_HUB_CITIES as readonly string[]).includes(
      args.citySlug
    )
  ) {
    return false
  }
  if (args.category === DirectoryCategory.Storage) return true
  const meta = args.location.directoryMeta
  return Boolean(
    args.location.directoryHasContact &&
    (displayableDirectoryHours(meta?.hours) ||
      meta?.priceFromMxn ||
      meta?.logoUrl)
  )
}

/**
 * SERP display budgets. Titles truncate around ~580px (~60 chars); metas
 * around ~155. Jules's rule for the lift: never ship a title/description
 * over budget — long facility names get word-boundary-truncated instead.
 */
export const DIRECTORY_SEO_TITLE_MAX = 60
export const DIRECTORY_SEO_DESCRIPTION_MAX = 155

/** Word-boundary truncation with an ellipsis; hard cut only for one giant word. */
export function truncateAtWord(value: string, max: number): string {
  if (value.length <= max) return value
  const slice = value.slice(0, Math.max(0, max - 1))
  const cut = slice.lastIndexOf(' ')
  const kept = cut > max * 0.5 ? slice.slice(0, cut) : slice
  return `${kept.trimEnd()}…`
}

/**
 * Fit SEO text into a budget: try each candidate template (richest first);
 * the first one within budget wins. If even the tersest overflows, its NAME
 * gets truncated to fit — the fixed copy never does, so the disclaimer and
 * brand suffix always survive intact.
 */
export function fitSeoText(
  candidates: ((name: string) => string)[],
  name: string,
  max: number
): string {
  for (const build of candidates) {
    const text = build(name)
    if (text.length <= max) return text
  }
  const last = candidates[candidates.length - 1]
  if (!last) return ''
  const overhead = last('').length
  const budget = Math.max(12, max - overhead)
  return last(truncateAtWord(name, budget))
}
